import React, { useEffect, useState } from 'react';
import { useDispatch } from "react-redux";
import { Formik } from 'formik';
import { Modal, ModalHeader, ModalBody, ModalFooter, Row, Col } from "reactstrap";
import { countryList } from "../../utilities/countries";
import Select from "react-select";
import { Table, TableHead, TableBody, TableRow, TableCell, Snackbar, Typography, Box } from '@mui/material';
import { Alert } from "@material-ui/lab";
import { clearAllUserListMessages } from "./../../store/actions";
import { ACCOUNT_DETAIL_FONT_SX, ACCOUNT_DETAIL_FONT_FAMILY } from "../AccountDetailPolicy/accountDetailTypography";

import VisibilityOff from "@material-ui/icons/VisibilityOff";
import Visibility from "@material-ui/icons/Visibility";
import IconButton from "@material-ui/core/IconButton";
import EncryptPassword from '../EncryptPassword';
import getUserRole from '../../helpers/jwt-token-access/userRole';
import 'react-phone-number-input/style.css'
import PhoneInput, {parsePhoneNumber, isPossiblePhoneNumber, getCountryCallingCode  } from 'react-phone-number-input';
import { ExtractCountryCallingCode, ExtractPhoneNumber } from './ExtractPhoneNumber';

const BRAND_ORANGE = "#F98125";

const CELL_TEXT = {
  fontSize: "0.8125rem",
  fontWeight: 500,
  color: "#334155",
  lineHeight: 1.4,
};

const HEADER_CELL_SX = {
  ...CELL_TEXT,
  fontWeight: 600,
  fontSize: "0.6875rem",
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: "#64748b",
  py: 1,
  px: 1.25,
  borderBottom: "1px solid #e2e8f0",
  backgroundColor: "#f8fafc",
  whiteSpace: "nowrap",
  position: "sticky",
  top: 0,
  zIndex: 1,
};

const BODY_CELL_SX = {
  ...CELL_TEXT,
  py: 1,
  px: 1.25,
  borderBottom: "1px solid #f1f5f9",
  verticalAlign: "middle",
};

const FORM_WRAPPER_SX = {
  ...ACCOUNT_DETAIL_FONT_SX,
  "& label": {
    fontSize: "0.8125rem",
    fontWeight: 600,
    color: "#334155",
    marginBottom: "6px",
  },
  "& .form-control, & .custom-select": {
    fontSize: "0.875rem",
    borderColor: "#e2e8f0",
    borderRadius: "8px",
    minHeight: 40,
    color: "#334155",
    boxShadow: "none",
  },
  "& .form-control:focus, & .custom-select:focus": {
    borderColor: "#111827",
    boxShadow: "0 0 0 1px #111827",
  },
  "& .form-group": {
    marginBottom: "1rem",
  },
  "& .PhoneInput": {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  "& .PhoneInputInput": {
    flex: 1,
    fontSize: "0.875rem",
    border: "1px solid #e2e8f0",
    borderRadius: "8px",
    minHeight: 40,
    padding: "8px 12px",
    color: "#334155",
  },
  "& .PhoneInputInput:focus": {
    outline: "none",
    borderColor: "#111827",
    boxShadow: "0 0 0 1px #111827",
  },
};

const ACCOUNT_DETAIL_SELECT_STYLES = {
  control: (base, state) => ({
    ...base,
    minHeight: 42,
    borderRadius: 8,
    borderColor: state.isFocused ? "#111827" : "#e2e8f0",
    boxShadow: "none",
    backgroundColor: "#fff",
    fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
    fontSize: "0.875rem",
    color: "#111827",
    "&:hover": { borderColor: "#cbd5e1" },
  }),
  input: (base) => ({
    ...base,
    fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
    fontSize: "0.875rem",
    color: "#111827",
  }),
  singleValue: (base) => ({
    ...base,
    fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
    fontSize: "0.875rem",
    fontWeight: 500,
    color: "#111827",
  }),
  placeholder: (base) => ({
    ...base,
    fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
    fontSize: "0.875rem",
    color: "#94a3b8",
  }),
  menu: (base) => ({
    ...base,
    fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
    fontSize: "0.875rem",
    borderRadius: 8,
    overflow: "hidden",
    zIndex: 9999,
  }),
  option: (base, state) => ({
    ...base,
    fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
    fontSize: "0.875rem",
    fontWeight: state.isSelected ? 600 : 500,
    color: state.isSelected ? "#fff" : "#111827",
    backgroundColor: state.isSelected
      ? state.isFocused
        ? "#e8741c"
        : "#F98125"
      : state.isFocused
        ? "#fff4eb"
        : "#fff",
    cursor: "pointer",
    ":active": {
      backgroundColor: state.isSelected ? "#e8741c" : "#fff4eb",
    },
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 1400,
    fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
  }),
};

const ACCOUNT_DETAIL_MULTI_SELECT_STYLES = {
  ...ACCOUNT_DETAIL_SELECT_STYLES,
  multiValue: (base) => ({
    ...base,
    backgroundColor: "#fff4eb",
    borderRadius: 6,
    border: "1px solid #fed7aa",
  }),
  multiValueLabel: (base) => ({
    ...base,
    fontFamily: ACCOUNT_DETAIL_FONT_FAMILY,
    fontSize: "0.8125rem",
    fontWeight: 500,
    color: "#9a3412",
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "#9a3412",
    ":hover": {
      backgroundColor: "#F98125",
      color: "#fff",
    },
  }),
};

const SELECT_MENU_PROPS = {
  menuPortalTarget: typeof document !== "undefined" ? document.body : null,
  menuPosition: "fixed",
  menuPlacement: "auto",
  menuShouldScrollIntoView: false,
  maxMenuHeight: 240,
};

const FieldLabel = ({ htmlFor, children, required = false, error, showError }) => (
  <Box
    component="label"
    htmlFor={htmlFor}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 0.75,
      flexWrap: "wrap",
      mb: "6px",
      fontSize: "0.8125rem",
      fontWeight: 600,
      color: "#334155",
    }}
  >
    <Box component="span">
      {children}
      {required ? (
        <Box component="span" sx={{ color: "#dc2626", ml: 0.25 }}>
          *
        </Box>
      ) : null}
    </Box>
    {showError && error ? (
      <Box
        component="span"
        sx={{ color: "#dc2626", fontSize: "0.75rem", fontWeight: 500 }}
      >
        {error}
      </Box>
    ) : null}
  </Box>
);

const AddNewUser = ({ addNewUserResponse, editUserResponse, organizations = {}, usersConfig, accounts, selectedUser, showAdduser, setShowAddUsers, handleOnSubmit }) => {
    // const { data, status } = addNewUserResponse || {};
    const dispatch = useDispatch();
    const { addNewUserMessage, addNewUserLoading, addNewUserError } = addNewUserResponse;
    const { editUserMessage, editUserLoading, editUserError } = editUserResponse;
    const { resourceGroups } = organizations;
    // const [emailDomains, setEmailDomains] = useState("");
    const [formikParams, setFormikParams] = useState();
    const [initValues, setInitValues] = useState({
        firstName: '',
        lastName: '',
        userName: '',
        password: '',
        email: '',
        emailDomain: '',
        country: '',
        phoneNumber: '',
        countryCode: '',
        orgCode: organizations.orgcode,
        acccodes: [],
        userRolesDTOS: [],
        resourceGroupDTOS: []
    });
    const [isEditFlow, setEditFlow] = useState(false);

    const [showPassword, setShowPassword] = useState(false);
    const userRole = getUserRole();

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    }

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    const validateEmailExistence = (formValues) => {
        // console.log("validateEmailExistence called");
        // console.log("validateEmailExistence called, props:", this.props);
        if(formValues.email && usersConfig) {
            // console.log("validateEmailExistence executed");
            const users  = usersConfig;
            const userExists = users.some(user => user.email.toLowerCase() === formValues.email.toLowerCase()+formValues.emailDomain);
            if(userExists) 
                return "This user already exists.";
            else 
                return null;
        }
    }

    const passwordRegex = /^(?!.*\s)(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~])[A-Za-z\d!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]{8,}$/;

    // To populate select options for email domain field
    const orgBillingEmail = organizations?.orgbillingemail;
    useEffect(() => {
        // console.log("organizations = ", organizations);
        // console.log("################################ orgBillingEmail = ", orgBillingEmail);
        if(orgBillingEmail){
            // setEmailDomains( [...new Set(["@"+orgBillingEmail.split("@").pop(),"@cloudnovatech.com"])] );
            setInitValues({...initValues, emailDomain: "@"+orgBillingEmail.split("@").pop()});
        }
    }, [orgBillingEmail]);

    useEffect(() => {
        if (selectedUser && Object.keys(selectedUser).length > 0) {
            const acccodeList = [];
            const emailParts = selectedUser.email.split("@");
            console.log("🚀 ~ AddNewUser ~ selectedUser:", selectedUser)

            selectedUser.listacct.filter(item => item.accountNumber != "INIT00000001").forEach((item) =>
                acccodeList.push(item.accountCode));
            setEditFlow(true);
            // console.log("init group", selectedUser.userGroup)
            setInitValues({
                //edit user
                firstName: selectedUser.firstname,
                lastName: selectedUser.lastName,
                userName: selectedUser.username,
                // email: selectedUser.email,
                email: emailParts[0],
                emailDomain: "@"+emailParts[1],
                country: selectedUser.country,
                phoneNumber: selectedUser.phoneNumber,
                orgCode: organizations.orgcode,
                userRolesDTOS: selectedUser.roles ? [selectedUser.roles] : [],
                resourceGroupDTOS: selectedUser.userGroup,
                acccodes: acccodeList,
                isEditFlow: true
            });

        } 
        else {
            //add user
            if (isEditFlow){
                setEditFlow(false);
                setInitValues({
                    ...initValues,
                    firstName: '',
                    lastName: '',
                    userName: '',
                    password: '',
                    email: '',
                    // emailDomain: '@cloudnovatech.com',
                    country: '',
                    phoneNumber: '',
                    countryCode: '',
                    orgCode: organizations.orgcode,
                    acccodes: [],
                    userRolesDTOS: Array.isArray(selectedUser.userRolesDTOS)
                        ? selectedUser.userRolesDTOS
                        : selectedUser.userRolesDTOS
                        ? [selectedUser.userRolesDTOS]
                        : [],
                    resourceGroupDTOS: []
                })            
            } 
        }

    }, [selectedUser])

    //For displaying error message of user creation
    useEffect(() => {
        if (addNewUserError || addNewUserMessage || editUserError || editUserMessage) {
            setTimeout(() => {
                console.log("About to call clearAllUserListMessages");
                dispatch(clearAllUserListMessages());

          }, 10000); // Hide all messages after 10 seconds
        }
    }, [addNewUserMessage, addNewUserError, editUserMessage, editUserError]);

    useEffect(() => {
        if (addNewUserMessage || editUserMessage) {
            if (showAdduser){
                setShowAddUsers(false);
            }             
        }
    }, [setShowAddUsers, addNewUserMessage, editUserMessage]);

    const onSubmitHandler = async (values, { setSubmitting }) => {
        try {
          setSubmitting(true);
    
          // Modify values as needed (e.g., concatenate email and emailDomain)
          const userRequest = { ...values };
          userRequest.email = userRequest.email + userRequest.emailDomain;
          delete userRequest.emailDomain;
    
          const encryptedPassword = await EncryptPassword(userRequest.password);
          userRequest.password = encryptedPassword;
    
          // Modify phoneNumber and countryCode synchronously
          userRequest.countryCode = ExtractCountryCallingCode(userRequest.phoneNumber);
          userRequest.phoneNumber = ExtractPhoneNumber(userRequest.phoneNumber);

          // Ensure userRolesDTOS is properly formatted
          // userRolesDTOS comes as array of strings, need to map to objects with roleName as string
          userRequest.userRolesDTOS = values.userRolesDTOS.map(role => {
            // If role is an array, take the first element
            const roleValue = Array.isArray(role) ? role[0] : role;
            return { roleName: roleValue };
          });

          console.log("Modified userRequest:", userRequest);
    
          // Handle form submission (e.g., API call) with modified userRequest
          handleOnSubmit(userRequest);
    
        } catch (error) {
          console.error("Error during form submission:", error);
          // Optionally handle error (e.g., show error message to the user)
        } finally {
          setSubmitting(false);
        }
      };

    return (
        <>
            <Modal
                isOpen={showAdduser}
                size="lg"
                centered
                toggle={() => setShowAddUsers(false)}
                contentClassName="border-0"
                style={ACCOUNT_DETAIL_FONT_SX}
            >
            <ModalHeader
                toggle={() => setShowAddUsers(false)}
                style={{
                    backgroundColor: BRAND_ORANGE,
                    margin: -1,
                    display: "flex",
                    alignItems: "center",
                    minHeight: 56,
                    borderBottom: "none",
                    borderTopLeftRadius: 12,
                    borderTopRightRadius: 12,
                }}
                close={
                    <button
                        type="button"
                        aria-label="Close"
                        onClick={() => setShowAddUsers(false)}
                        style={{
                            padding: 0,
                            margin: 0,
                            border: "none",
                            background: "transparent",
                            color: "#FFFFFF",
                            fontSize: 28,
                            fontWeight: 700,
                            lineHeight: 1,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            opacity: 1,
                            boxShadow: "none",
                        }}
                    >
                        <span aria-hidden="true">×</span>
                    </button>
                }
            >
                <Typography sx={{ fontWeight: 700, fontSize: "1.0625rem", color: "#FFFFFF", lineHeight: 1.3 }}>
                    {isEditFlow ? "Edit User" : "Add New User"}
                </Typography>
            </ModalHeader>
            <Formik initialValues={initValues}
                validateOnMount={true}
                validate={(values) => {
                    const errors = {};
                    ['firstName',
                        'lastName',
                        'userName',
                        'email',
                        'country',
                        'phoneNumber'].forEach((item) => {
                            if (!values[item]) {
                                errors[item] = 'Required'
                            }
                        })
                    if(!isEditFlow){
                        if (!values.password) {
                            errors.password = "Password is required.";
                          } else if (!passwordRegex.test(values.password)) {
                            errors.password =
                              "Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one digit, and one special character."; 
                        }
                    }
                    
                    if (values.acccodes && values.acccodes.length === 0) {
                        if (!(values.resourceGroupDTOS && values.resourceGroupDTOS.length > 0)) {
                            errors.acccodes = 'Select either Resource Group or Account'
                        }
                    }
                    if (values.resourceGroupDTOS && values.resourceGroupDTOS.length === 0) {
                        if (!(values.acccodes && values.acccodes.length > 0)) {
                            errors.resourceGroupDTOS = 'Select either Resource Group or Account'
                        }
                    }
                    if (values.userRolesDTOS && values.userRolesDTOS.length === 0) {
                        errors.userRolesDTOS = 'Select a Role'
                    }
                    if (values.phoneNumber) {
                        const phoneNumber = values.phoneNumber;
                        // console.log('--== _addNewUser :::> ', phoneNumber);
                        if (!isPossiblePhoneNumber(phoneNumber)) {
                            errors.phoneNumber = 'Please enter a valid phone number'
                        }
                    }

                    // console.log('--== _addNewUser errors', errors);

                    // const regex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                    const regex = /^[^\s@]+$/;
                    if (values.email && !regex.test(String(values.email).toLowerCase())) {
                        errors.email = 'Please enter a valid email id'
                    } else if (!isEditFlow) {
                        if(validateEmailExistence(values)){
                            errors.email = validateEmailExistence(values);
                        }                        
                    }
                    console.log(" errors: ", errors);
                    return errors;
                }}
                onSubmit={onSubmitHandler}
                enableReinitialize
            >
                {({ values, errors, isValid, isSubmitting, touched, setFieldValue, handleSubmit, handleChange, handleBlur, setFieldTouched }) => {

                    const handleMultiSelectChange = (event) => {

                        // console.log("event is1212", event)
                        setFieldTouched("resourceGroupDTOS", true, false)
                        var gprs = []
                        if (event != null) {
                            if (isEditFlow) {
                                event.map((grp) => {
                                console.log("🚀 ~ handleMultiSelectChange ~ grp:", grp)

                                    gprs.push({ resourcename: grp.value, id: grp.id })

                                })
                            }
                            else {
                                event.map((grp) => {

                                    gprs.push({ resourcename: grp.value })

                                })
                            }
                        }
                        setFieldValue("resourceGroupDTOS", gprs);
                        // console.log("resourcegrps69", values.resourceGroupDTOS)
                    }

                    const handlePhoneChange = (value) => {
                        setFieldValue("phoneNumber", value);
                    }
                
                    const selectedCount = values.acccodes.length;
                    const availableCount = accounts.length - selectedCount;
                    const selectedResourceGroups = values.resourceGroupDTOS.map((rg) => rg.resourcename);

                    const countryOptions = countryList.map((country) => ({
                      value: country.name,
                      label: country.name,
                    }));

                    const roleOptions = [
                      ...(userRole === "Administrator"
                        ? [{ value: "Administrator", label: "Administrator" }]
                        : []),
                      { value: "PowerUser", label: "PowerUser" },
                      { value: "StandardUser", label: "StandardUser" },
                    ];

                    // Filter the resource groups based on the selectedResourceGroups
                    const filteredAccounts = organizations.resourceGroups
                      .filter((orgGrp) => selectedResourceGroups.includes(orgGrp.resourcename))
                      .flatMap((accGrp) => accGrp.acccodes);
                     values.acccodes = filteredAccounts.map((acc) => acc.accountCode);

                    return (
                        <>
                            <ModalBody
                                style={{
                                    maxHeight: "calc(100vh - 120px)",
                                    overflowY: "auto",
                                    overflowX: "hidden",
                                    padding: "1.25rem 1.5rem",
                                    backgroundColor: "#fff",
                                }}
                            >
                                <Box sx={FORM_WRAPPER_SX}>
                                <div className="row">
                                    <div className="col-sm-12 col-md-6">
                                        <div className="form-group">
                                            <FieldLabel
                                                htmlFor="firstNameField"
                                                required
                                                error={errors.firstName}
                                                showError={touched.firstName}
                                            >
                                                First Name
                                            </FieldLabel>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="firstNameField"
                                                name="firstName"
                                                aria-describedby="First Name"
                                                placeholder="First Name"
                                                value={values.firstName}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                required />
                                        </div>
                                        <div className="form-group">
                                            <FieldLabel
                                                htmlFor="lastNameField"
                                                required
                                                error={errors.lastName}
                                                showError={touched.lastName}
                                            >
                                                Last Name
                                            </FieldLabel>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="lastNameField"
                                                name="lastName"
                                                aria-describedby="Last Name"
                                                placeholder="Last Name"
                                                value={values.lastName}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                required />
                                        </div>
                                        <div className="row">
                                            <div className="col-sm-12 col-md-12 col-lg-12 col-xl-12">
                                                <div className="form-group">
                                                    <FieldLabel
                                                        htmlFor="emailField"
                                                        required
                                                        error={errors.email}
                                                        showError={touched.email}
                                                    >
                                                        Email / User Name
                                                    </FieldLabel>
                                                    <Row>
                                                        <Col lg="6" md="6" sm="6" style={{paddingRight: "0px"}}>
                                                            <input
                                                                type="email"
                                                                className="form-control"
                                                                id="emailField"
                                                                name="email"
                                                                aria-describedby="Email"
                                                                placeholder="Email"
                                                                value={values.email}
                                                                onChange={(e) => {
                                                                    // console.log("### emailDomain : ", values);
                                                                    setFieldValue("email", e.target.value);
                                                                    setFieldValue("userName", e.target.value+values.emailDomain);
                                                                }}
                                                                onBlur={handleBlur}
                                                                disabled={isEditFlow}
                                                                required
                                                                style={{borderTopRightRadius: "0px", borderBottomRightRadius: "0px"}} 
                                                            />
                                                        </Col>
                                                        <Col lg="6" md="6" sm="6" style={{paddingLeft: "0px"}}>
                                                            <input 
                                                                required 
                                                                className="form-control" 
                                                                id="emailDomainField" 
                                                                name="emailDomain" 
                                                                value={values.emailDomain}
                                                                onChange={handleChange}
                                                                // onBlur={handleBlur}
                                                                style={{
                                                                    borderTopLeftRadius: "0px", 
                                                                    borderBottomLeftRadius: "0px", 
                                                                    borderLeft: "0px", 
                                                                    backgroundColor: "#f8fafc",
                                                                    color: "#64748b",
                                                                }}
                                                                disabled
                                                            />
                                                        </Col>
                                                    </Row>                                         
                                                </div>
                                            </div>
                                        </div>
                                        {
                                            !isEditFlow && (
                                                <div className="form-group">
                                                    <FieldLabel
                                                        htmlFor="passwordField"
                                                        required
                                                        error={errors.password}
                                                        showError={touched.password}
                                                    >
                                                        Password
                                                    </FieldLabel>
                                                    <div style={{position: "relative"}}>
                                                        <input
                                                            type={showPassword ? "text" : "password"}
                                                            className="form-control"
                                                            id="passwordField"
                                                            name="password"
                                                            aria-describedby="Password"
                                                            placeholder="Password"
                                                            value={values.password}
                                                            onChange={handleChange}
                                                            onBlur={handleBlur}
                                                            required />
                                                        <IconButton
                                                            onClick={handleClickShowPassword}
                                                            onMouseDown={handleMouseDownPassword}
                                                            style={{
                                                                position: "absolute",
                                                                top: "50%",
                                                                right: "5px",
                                                                transform: "translateY(-50%)",
                                                                cursor: "pointer",
                                                                fontSize: "20px"
                                                            }}
                                                        >
                                                            {showPassword ? (
                                                            <Visibility />
                                                            ) : (
                                                            <VisibilityOff />
                                                            )}
                                                        </IconButton>
                                                    </div>
                                                </div>
                                            )
                                        }
                                        <div className="form-group">
                                            <FieldLabel
                                                htmlFor="phoneNumberField"
                                                required
                                                error={errors.phoneNumber}
                                                showError={touched.phoneNumber}
                                            >
                                                Phone Number
                                            </FieldLabel>
                                            <PhoneInput
                                                type="text"
                                                id="phoneNumberField"
                                                name="phoneNumber"
                                                aria-describedby="Phone Number"
                                                placeholder="Phone Number"
                                                defaultCountry="US"
                                                value={values.phoneNumber}
                                                maxLength={16}
                                                onChange={handlePhoneChange}
                                                onBlur={handleBlur}
                                                required />
                                        </div>
                                        <div className="form-group" style={{ display: 'none' }}>
                                            <label htmlFor="userNameField">User Name</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="userNameField"
                                                name="userName"
                                                aria-describedby="User Name"
                                                placeholder="User Name"
                                                value={values.userName}
                                                onBlur={handleBlur}
                                                disabled={true}
                                                required />
                                            {errors.userName 
                                            && touched.userName 
                                            && (
                                                <div className="text-danger">{errors.userName}</div>
                                            )}

                                        </div>
                                    </div>
                                    <div className="col-sm-12 col-md-6">

                                        <div className="form-group">
                                            <FieldLabel
                                                htmlFor="countryField"
                                                required
                                                error={errors.country}
                                                showError={touched.country}
                                            >
                                                Country
                                            </FieldLabel>
                                            <Select
                                                inputId="countryField"
                                                name="country"
                                                options={countryOptions}
                                                placeholder="Choose..."
                                                value={
                                                  values.country
                                                    ? {
                                                        value: values.country,
                                                        label: values.country,
                                                      }
                                                    : null
                                                }
                                                onChange={(option) =>
                                                  setFieldValue("country", option ? option.value : "")
                                                }
                                                onBlur={() => setFieldTouched("country", true)}
                                                styles={ACCOUNT_DETAIL_SELECT_STYLES}
                                                {...SELECT_MENU_PROPS}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <FieldLabel
                                                htmlFor="roleField"
                                                required
                                                error={errors.userRolesDTOS}
                                                showError={touched.userRolesDTOS}
                                            >
                                                Role
                                            </FieldLabel>
                                            <Select
                                                inputId="roleField"
                                                name="userRolesDTOS"
                                                options={roleOptions}
                                                placeholder="Select a Role"
                                                value={
                                                  values.userRolesDTOS[0]
                                                    ? {
                                                        value: values.userRolesDTOS[0],
                                                        label: values.userRolesDTOS[0],
                                                      }
                                                    : null
                                                }
                                                onChange={(option) =>
                                                  setFieldValue(
                                                    "userRolesDTOS",
                                                    option ? [option.value] : []
                                                  )
                                                }
                                                onBlur={() => setFieldTouched("userRolesDTOS", true)}
                                                styles={ACCOUNT_DETAIL_SELECT_STYLES}
                                                {...SELECT_MENU_PROPS}
                                            />
                                        </div>
                                        <div className="form-group" style={{ marginBottom: "1rem" }}>
                                            <FieldLabel
                                                htmlFor="resourceGroupField"
                                                required
                                                error={errors.resourceGroupDTOS || errors.acccodes}
                                                showError={touched.resourceGroupDTOS || touched.acccodes}
                                            >
                                                Resource Groups
                                            </FieldLabel>
                                        <Select
                                            inputId="resourceGroupField"
                                            name="rgs"
                                            closeMenuOnSelect={false}
                                            onChange={handleMultiSelectChange}
                                            isMulti
                                            isClearable
                                            placeholder="Resource Groups ..."
                                            styles={ACCOUNT_DETAIL_MULTI_SELECT_STYLES}
                                            {...SELECT_MENU_PROPS}
                                            value={
                                                values.resourceGroupDTOS.map((rg) => {
                                                console.log("🚀 ~ rg:", rg)

                                                    return { value: rg.resourcename, label: rg.resourcename, id: rg.id }
                                                })}
                                            options={resourceGroups.map((rg) => {
                                                return { value: rg.resourcename, label: rg.resourcename, id: rg.id }
                                            })}
                                        />
                                        </div>

                                        <div className="form-group" style={{ marginBottom: 0 }}>
                                            <label htmlFor="accountField">Cloud Accounts</label>
                                            <Box
                                              sx={{
                                                border: "1px solid #e2e8f0",
                                                borderRadius: "8px",
                                                overflow: "hidden",
                                                height: 200,
                                                overflowY: "auto",
                                                backgroundColor: "#fff",
                                              }}
                                            >
                                            <Table size="small" stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ ...HEADER_CELL_SX, width: "50%" }}>Account Name</TableCell>
                                                        <TableCell sx={{ ...HEADER_CELL_SX, width: "50%" }}>Account Number</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                {filteredAccounts.map((acc) => {
                                                    const accountNumber =
                                                        acc.accountNumber ||
                                                        (accounts || []).find(
                                                            (a) => a.accountCode === acc.accountCode
                                                        )?.accountNumber ||
                                                        "";
                                                    return (
                                                            <TableRow key={acc.accountCode}>
                                                                <TableCell sx={BODY_CELL_SX}>{acc.accountName}</TableCell>
                                                                <TableCell sx={BODY_CELL_SX}>{accountNumber}</TableCell>
                                                            </TableRow>
                                                            )
                                                    })
                                                }
                                                </TableBody>
                                            </Table>
                                            </Box>
                                        </div>
                                    </div>
                                </div>
                                </Box>
                            </ModalBody>
                            <ModalFooter
                                style={{
                                    width: "100%",
                                    borderTop: "1px solid #e5e7eb",
                                    backgroundColor: "#f8fafc",
                                    padding: "1rem 1.5rem",
                                    borderBottomLeftRadius: 12,
                                    borderBottomRightRadius: 12,
                                }}
                            >
                                <div
                                    style={{
                                        width: "100%",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            gap: "12px",
                                            alignItems: "center",
                                        }}
                                    >
                                        <button
                                            type="button"
                                            className="btn w-lg btn-secondary"
                                            onClick={() => setShowAddUsers(false)}
                                        >
                                            Close
                                        </button>
                                    </div>
                                    <div>
                                        <button
                                            type="button"
                                            className="btn w-lg btn-primary"
                                            disabled={!isValid || addNewUserLoading || editUserLoading}
                                            onClick={handleSubmit}
                                        >
                                            Submit
                                        </button>
                                    </div>
                                </div>
                            </ModalFooter>

                        </>
                    )
                }}
            </Formik>
            </Modal>
            <Snackbar
                open={addNewUserError!==null}
                autoHideDuration={5000}
            >
                <Alert severity="error">{addNewUserError}</Alert>
            </Snackbar>
            <Snackbar
                open={addNewUserMessage!==null}
                autoHideDuration={5000}
            >
                <Alert severity="success">{addNewUserMessage}</Alert>
            </Snackbar>
            <Snackbar
                open={editUserError!==null}
                autoHideDuration={5000}
            >
                <Alert severity="error">{editUserError}</Alert>
            </Snackbar>
            <Snackbar
                open={editUserMessage!==null}
                autoHideDuration={5000}
            >
                <Alert severity="success">{editUserMessage}</Alert>
            </Snackbar>
        </>        
    )

}

export default AddNewUser;